import React, {useEffect, useMemo, useState} from "react";
import IconButton from "../../../base/button/icon_button";
import {Close} from "@mui/icons-material";
import Divider from "@mui/material/Divider";
import LazyImage from "../../../app-specific/lazy-image";
import OperatorsAutoComplete from "../auto-complete";
import {Row} from "reactstrap";
import Button from "../../../base/button/button";
import {Popover, useTheme} from "@mui/material";
import {ReactComponent as CheckedBox} from "../../../../../assets/images/check-box/checkbox-active.svg";
import {ReactComponent as EmptyBox} from "../../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as ImagePlaceholder} from "../../../../../assets/images/operator-profile-placeholder/operator-profile-placeholder-sidebar.svg";
import Utils from "../../../../../core/services/utils/utils";
import {useOperatorInformation} from "../../../../hooks/use-operator-information";
import CacheService from "../../../../../core/services/cache/cache_service";

const AssignOperatorsPopover = ({
                                    loading,
                                    anchorEl,
                                    setAnchorEl,
                                    assignedOperators,
                                    setAssignedOperators,
                                }) => {
    const [autoCompleteSelectedOperators, setAutoCompleteSelectedOperators] = useState([]);
    const [currentOperatorSelected, setCurrentOperatorSelected] = useState(false);
    const operatorInformation = useOperatorInformation();
    const theme = useTheme();

    /**
     * Memo version of indicator for getting if the pop over is open or not
     * @type {boolean}
     */
    const isOpen = useMemo(() => Boolean(anchorEl), [anchorEl]);

    /**
     * With initial render of the component:
     * Populates the pendingAssignedOperators same as assigned operators
     */
    useEffect(() => {
        if (isOpen && !!operatorInformation) {
            setAutoCompleteSelectedOperators(assignedOperators?.filter(e => e.id !== operatorInformation?.id));
            setCurrentOperatorSelected(assignedOperators?.map(e => e?.id)?.includes(operatorInformation?.id) ?? false);
        }
    }, [assignedOperators, isOpen, operatorInformation])

    /**
     * Closes the assign operators popover
     * @param event
     */
    const handlePopOverClose = (event) => {
        if (event.target.closest('.assign-operator-pop-over-content')) {
            event.preventDefault();
        }
        setAnchorEl(null);
    }

    /**
     * un-assigns all operators from the list of assignees
     * @param e
     */
    const clearAllAssignedOperators = (e) => {
        setAssignedOperators([]);
        handlePopOverClose(e);
    }

    /**
     * Assigns the selected operators to the task
     * @param e
     * @return {Promise<void>}
     */
    const assignOperators = async (e) => {
        handlePopOverClose(e);
        await Utils.wait(300);
        setAssignedOperators([
            (currentOperatorSelected ? operatorInformation : undefined),
            ...autoCompleteSelectedOperators
        ].filter(e => !!e));
    }

    /**
     * Toggles current operator being assign to the task by adding or removing it from the pendingAssignedOperators
     */
    const toggleCurrentOperatorBeingAssigned = () => {
        setCurrentOperatorSelected(prevState => !prevState);
    }

    return (
        <Popover
            disableRestoreFocus
            open={isOpen}
            onClose={() => setAnchorEl(null)}
            classes={{
                paper: 'assign-operator-popover',
            }}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
        >
            <div
                className={'assign-operator-pop-over-content'}
            >
                <div className={'header'}>
                    <div style={{width: '24px'}}/>
                    <p>Assign</p>
                    <IconButton
                        color={"paper.primary"}
                        onClick={handlePopOverClose}
                    >
                        <Close className='icon small'/>
                    </IconButton>
                </div>
                <Divider className='divider'/>
                <div
                    className={'current-operator'}
                    onClick={toggleCurrentOperatorBeingAssigned}
                >
                    <div className={'operator-details'}>
                        <div className={'option-image'}>
                            <LazyImage
                                alt={'profile'}
                                noImage={<div style={{backgroundColor: `${theme.palette.background.default}`}}>
                                    <ImagePlaceholder/>
                                </div>}
                                src={CacheService.getOperatorProfileImage(operatorInformation?.id)}
                                height={35}
                            />
                        </div>
                        <div className={'current-operator-title'}>
                            <p className={'ml-2'}>{operatorInformation?.name ?? ""}</p>
                        </div>
                    </div>
                    {
                        currentOperatorSelected
                            ? <CheckedBox/>
                            : <EmptyBox/>
                    }
                </div>
                <div className={'px-4 w-100'}>
                    <OperatorsAutoComplete
                        multiple={false}
                        selectedItems={autoCompleteSelectedOperators}
                        setSelectedItems={(selectedOptions) => setAutoCompleteSelectedOperators(prevState => {
                            const allSelectedItems = [...prevState, ...selectedOptions];
                            return Array.from(new Set(allSelectedItems.map(e => e.id)))
                                .map(id => allSelectedItems.find(e => e.id === id))
                                .filter(t => selectedOptions.map(e => e.id).includes(t.id));
                        })}
                        currentOperatorId={operatorInformation?.id}
                    />
                </div>
                <div className={'footer'}>
                    <Divider className='divider'/>
                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                        <Button
                            disabled={loading}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={clearAllAssignedOperators}
                            borderRadius={'0 8px 0 8px'}
                        >
                            {loading ? 'loading...' : 'Clear all'}
                        </Button>
                        <Button
                            disabled={loading}
                            variant={'dialog'}
                            color={'primary'}
                            type={'submit'}
                            borderRadius={'8px 0 8px 0'}
                            onClick={assignOperators}
                        >
                            {loading ? 'loading...' : 'Save'}
                        </Button>
                    </Row>
                </div>
            </div>
        </Popover>
    );
}

export default AssignOperatorsPopover;
